import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './client';

// Server: events/EventLandDto.java — the land an active event's vendor/quest-giver activities
// live in, resolved off the event's own bundle content (EventController.resolveLand); null when
// the event names no activity, or that activity/its land doc can't be resolved.
export type EventLandDto = {
  code: string;
  name: string;
};

// Server: events/EventActiveDto.java — GET /api/v1/events/active's one row. Deliberately narrow
// (code/name/endsAt/land ONLY) — never the event's own bundle internals (quest-chain codes, shop
// codes, cosmetic rewards, drop-table overrides): no datamine oracle for content a player hasn't
// reached yet. This is also why the event screen links its vendor card to the LAND screen rather
// than a direct `/world/shops/:shopCode` deep link (EventScreen.tsx's own header comment) — the
// vendor's shop code is exactly the kind of bundle-internal detail this DTO withholds.
export type EventActiveDto = {
  code: string;
  name: string;
  endsAt: string;
  land: EventLandDto | null;
};

export const eventsKeys = {
  active: ['events', 'active'] as const,
};

export const getActiveEvents = () => apiFetch<EventActiveDto[]>('/events/active');

/**
 * `staleTime: 5min` — same "slow-changing content, avoid a refetch on every sheet/screen mount"
 * reasoning `conversations.ts`'s own `usePhraseCategories` gives for its identical value: an
 * event's own window is measured in days/weeks (R1's `[startsAt, endsAt)`), so polling it on every
 * World-hub remount would be pure waste. The ribbon/HUD chip disappearing exactly at the window's
 * real boundary is a nice-to-have, not a correctness requirement this app makes anywhere else
 * either (e.g. a shop's own `nextRestockAt` clock is similarly coarse) — a stale-by-at-most-5-
 * minutes ribbon is an accepted tradeoff, not a bug.
 */
export function useActiveEvents() {
  return useQuery({ queryKey: eventsKeys.active, queryFn: getActiveEvents, staleTime: 5 * 60 * 1000 });
}
